import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../../api/api';
import { SaleCatalogAdminItem } from '../../types';
import OperatingDishCalculations from '../../components/OperatingDishCalculations';
import PublicMenuPublicationPanel from '../../components/PublicMenuPublicationPanel';

type GroupCatalogResponse = { version: number; items: Array<{ itemType: string; itemId: number }> };
type GroupDraft = { groupId: string; date: string; version: number; items: Set<string> };
type GroupLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

const apiMessage = (error: unknown, fallback: string) =>
  axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message
    ? error.response.data.message
    : fallback;

const isConflict = (error: unknown) => axios.isAxiosError(error) && error.response?.status === 409;

const typeLabels: Record<SaleCatalogAdminItem['itemType'], string> = {
  dish: 'Страва',
  service: 'Послуга',
  'menu-item': 'Позиція меню',
};

const todayInKyiv = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Kyiv', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const SaleCatalogPage: React.FC = () => {
  const [items, setItems] = useState<SaleCatalogAdminItem[]>([]);
  const [groups, setGroups] = useState<Array<{ id: number; name: string; isArchived: boolean }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(todayInKyiv);
  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [groupLoad, setGroupLoad] = useState<GroupLoadState>('idle');
  const groupRequest = useRef(0);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savingGroup, setSavingGroup] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catalogResponse, groupsResponse] = await Promise.all([
        api.get<SaleCatalogAdminItem[]>('/sale-catalog/manage'),
        api.get<Array<{ id: number; name: string; isArchived: boolean }>>('/operating-groups'),
      ]);
      setItems(catalogResponse.data);
      setGroups(groupsResponse.data);
      setError('');
    } catch {
      setError('Не вдалося завантажити каталог для налаштування.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Every group catalog response is keyed by request number and group/date identity so a late
  // response can never populate the draft that is submitted for another identity.
  const loadGroup = useCallback(async (groupId: string, date: string): Promise<'loaded' | 'failed' | 'stale'> => {
    const request = ++groupRequest.current;
    setGroupDraft(null);
    if (!groupId) {
      setGroupLoad('idle');
      return 'stale';
    }
    setGroupLoad('loading');
    try {
      const { data } = await api.get<GroupCatalogResponse>(
        `/operating-groups/${groupId}/sale-catalog`,
        { params: { onDate: date } }
      );
      if (request !== groupRequest.current) return 'stale';
      setGroupDraft({
        groupId,
        date,
        version: data.version,
        items: new Set(data.items.map((item) => `${item.itemType}:${item.itemId}`)),
      });
      setGroupLoad('loaded');
      return 'loaded';
    } catch (requestError: unknown) {
      if (request !== groupRequest.current) return 'stale';
      setGroupLoad('failed');
      setError(apiMessage(requestError, 'Не вдалося завантажити каталог групи.'));
      return 'failed';
    }
  }, []);

  useEffect(() => {
    void loadGroup(selectedGroupId, effectiveFrom);
  }, [effectiveFrom, selectedGroupId, loadGroup]);

  useEffect(() => {
    const requests = groupRequest;
    return () => { requests.current += 1; };
  }, []);

  const changeGroupIdentity = (groupId: string, date: string) => {
    groupRequest.current += 1;
    setGroupDraft(null);
    setGroupLoad(groupId ? 'loading' : 'idle');
    setMessage('');
    setSelectedGroupId(groupId);
    setEffectiveFrom(date);
  };

  const groupDraftReady = groupLoad === 'loaded' && groupDraft !== null &&
    groupDraft.groupId === selectedGroupId && groupDraft.date === effectiveFrom;
  const canEditGroup = groupDraftReady && !savingGroup;

  const updateItem = (key: string, changes: Partial<SaleCatalogAdminItem>) => {
    setItems((current) => current.map((item) =>
      `${item.itemType}-${item.id}` === key ? { ...item, ...changes } : item));
  };

  const save = async (item: SaleCatalogAdminItem) => {
    const key = `${item.itemType}-${item.id}`;
    if (item.isApprovedForSale && !item.effectiveFrom) {
      setError(`Для затвердження «${item.name}» вкажіть дату початку продажу.`);
      return;
    }
    if (item.effectiveFrom && item.effectiveTo && item.effectiveTo < item.effectiveFrom) {
      setError(`Дата завершення для «${item.name}» не може бути раніше дати початку.`);
      return;
    }

    setSavingKey(key);
    setError('');
    setMessage('');
    try {
      await api.put(`/sale-catalog/${item.itemType}/${item.id}`, {
        isApprovedForSale: item.isApprovedForSale,
        effectiveFrom: item.effectiveFrom || null,
        effectiveTo: item.effectiveTo || null,
      });
      setMessage(`Налаштування «${item.name}» збережено.`);
    } catch (saveError: unknown) {
      setError(apiMessage(saveError, `Не вдалося зберегти «${item.name}».`));
    } finally {
      setSavingKey(null);
    }
  };

  const saveGroupCatalog = async () => {
    const draft = groupDraft;
    if (!canEditGroup || !draft) return;
    const request = groupRequest.current;
    setSavingGroup(true);
    setError('');
    setMessage('');
    try {
      try {
        await api.put(`/operating-groups/${draft.groupId}/sale-catalog`, {
          version: draft.version,
          effectiveFrom: draft.date,
          items: Array.from(draft.items).map((key) => {
            const separator = key.indexOf(':');
            return { itemType: key.slice(0, separator), itemId: Number(key.slice(separator + 1)) };
          }),
        });
      } catch (saveError: unknown) {
        if (request !== groupRequest.current) return;
        setError(apiMessage(saveError, 'Не вдалося зберегти каталог операційної групи.'));
        if (!isConflict(saveError)) return;
        if (await loadGroup(draft.groupId, draft.date) === 'failed') {
          setError('Каталог змінився, а актуальну версію не вдалося завантажити. Повторіть завантаження перед збереженням.');
        }
        return;
      }
      if (request !== groupRequest.current) return;
      const reloaded = await loadGroup(draft.groupId, draft.date);
      if (reloaded === 'loaded') {
        setMessage('Контекстний каталог збережено. Вибір позицій сам по собі не активує контекстні ціни.');
      } else if (reloaded === 'failed') {
        setError('Каталог збережено, але його актуальний стан не вдалося завантажити. Повторіть завантаження перед наступною зміною.');
      }
    } finally {
      setSavingGroup(false);
    }
  };

  return (
    <div className="page-shell page-shell--wide">
      <h2>Затвердження каталогу продажу</h2>
      <PublicMenuPublicationPanel />
      <p style={{ color: 'var(--crm-text-muted)' }}>
        Клієнтам доступні лише затверджені позиції в межах указаних дат. Склад і рецептури не змінюються.
      </p>
      {error && <p role="alert" style={{ color: 'var(--crm-danger, #b42318)' }}>{error}</p>}
      {message && <p role="status">{message}</p>}
      {loading ? <p>Завантаження...</p> : items.length === 0 ? (
        <p>Каталог поки порожній.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--crm-text)' }}>
            <thead>
              <tr>
                <th scope="col" style={cellStyle}>Позиція</th>
                <th scope="col" style={cellStyle}>Категорія</th>
                <th scope="col" style={cellStyle}>Ціна</th>
                <th scope="col" style={cellStyle}>Початок продажу</th>
                <th scope="col" style={cellStyle}>Кінець продажу</th>
                <th scope="col" style={cellStyle}>Затвердження</th>
                <th scope="col" style={cellStyle}>Дія</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const key = `${item.itemType}-${item.id}`;
                return (
                  <tr key={key}>
                    <td style={cellStyle}>
                      <strong>{item.name}</strong>
                      <div style={{ color: 'var(--crm-text-muted)', fontSize: '12px' }}>{typeLabels[item.itemType]}</div>
                    </td>
                    <td style={cellStyle}>{item.category}</td>
                    <td style={cellStyle}>{item.price.toFixed(2)} ₴</td>
                    <td style={cellStyle}>
                      <input
                        aria-label={`Дата початку продажу: ${item.name}`}
                        type="date"
                        value={item.effectiveFrom ?? ''}
                        onChange={(event) => updateItem(key, { effectiveFrom: event.target.value || null })}
                      />
                    </td>
                    <td style={cellStyle}>
                      <input
                        aria-label={`Дата завершення продажу: ${item.name}`}
                        type="date"
                        value={item.effectiveTo ?? ''}
                        onChange={(event) => updateItem(key, { effectiveTo: event.target.value || null })}
                      />
                    </td>
                    <td style={cellStyle}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={item.isApprovedForSale}
                          aria-label={`Затверджено для продажу: ${item.name}`}
                          onChange={(event) => updateItem(key, { isApprovedForSale: event.target.checked })}
                        />
                        {item.isApprovedForSale ? 'Затверджено' : 'Не затверджено'}
                      </label>
                    </td>
                    <td style={cellStyle}>
                      <button type="button" disabled={savingKey === key} onClick={() => void save(item)}>
                        {savingKey === key ? 'Збереження...' : 'Зберегти'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && (
        <section aria-labelledby="operating-group-sale-catalog-title" style={{ marginTop: 32 }}>
          <h3 id="operating-group-sale-catalog-title">Каталог для операційної групи</h3>
          <p style={{ color: 'var(--crm-text-muted)' }}>
            Вибір лише звужує глобально затверджені позиції. Контекстна ціна страви потребує окремої активації розрахунку;
            собівартість, рецептури та склад клієнтам не передаються.
          </p>
          <label style={{ display: 'block', maxWidth: 520, marginBottom: 12 }}>
            Операційна група
            <select
              value={selectedGroupId}
              disabled={savingGroup}
              onChange={(event) => changeGroupIdentity(event.target.value, effectiveFrom)}
            >
              <option value="">Оберіть групу</option>
              {groups.filter((group) => !group.isArchived).map((group) =>
                <option key={group.id} value={group.id}>{group.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'block', maxWidth: 260, marginBottom: 12 }}>
            Каталог діє з
            <input
              type="date"
              value={effectiveFrom}
              min={todayInKyiv()}
              disabled={savingGroup}
              onChange={(event) => changeGroupIdentity(selectedGroupId, event.target.value)}
            />
          </label>
          {!selectedGroupId ? <p>Оберіть групу, щоб налаштувати її меню продажу.</p> : groupLoad === 'failed' ? (
            <div role="group" aria-label="Каталог групи не завантажено">
              <p>Каталог групи не завантажено. Збереження недоступне, доки актуальний стан не буде отримано.</p>
              <button
                type="button"
                disabled={savingGroup}
                onClick={() => {
                  setError('');
                  void loadGroup(selectedGroupId, effectiveFrom);
                }}
              >
                Повторити завантаження
              </button>
            </div>
          ) : !groupDraftReady || !groupDraft ? (
            <p role="status">Завантаження каталогу групи...</p>
          ) : (
            <>
              {items.map((item) => {
                const draftIdentity = { groupId: groupDraft.groupId, date: groupDraft.date };
                const key = `${item.itemType}:${item.id}`;
                const selected = groupDraft.items.has(key);
                const eligible = item.isApprovedForSale && Boolean(item.effectiveFrom) &&
                  item.effectiveFrom! <= effectiveFrom &&
                  (!item.effectiveTo || item.effectiveTo >= effectiveFrom);
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!canEditGroup || (!eligible && !selected)}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setGroupDraft((current) => {
                          if (!current || current.groupId !== draftIdentity.groupId || current.date !== draftIdentity.date) {
                            return current;
                          }
                          const next = new Set(current.items);
                          if (checked) next.add(key);
                          else next.delete(key);
                          return { ...current, items: next };
                        });
                      }}
                    />
                    <span>{item.name} · {item.price.toFixed(2)} ₴{!eligible ? ' · не затверджено на цю дату' : ''}</span>
                  </label>
                );
              })}
              <button type="button" disabled={!canEditGroup} onClick={() => void saveGroupCatalog()}>
                {savingGroup ? 'Збереження...' : 'Зберегти каталог групи'}
              </button>
            </>
          )}
        </section>
      )}
      {!loading && <OperatingDishCalculations groups={groups} dishes={items.filter((item) => item.itemType === 'dish')} />}
    </div>
  );
};

const cellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '10px 8px',
  borderBottom: '1px solid var(--crm-border)',
  verticalAlign: 'middle',
};

export default SaleCatalogPage;
